<script lang="ts">
  import { RefreshCw, X } from '@lucide/svelte';
  import type { EventAnalysisPresentation } from '../lib/event-analysis';
  import { formatCalibratedProbability } from '../lib/event-analysis';
  import { formatUtc } from '../lib/time';
  import type { ChartModel } from '../lib/types';
  import ChartHost from './ChartHost.svelte';

  export let plotId: string;
  export let timestampSeconds: number;
  export let keyboardActive: boolean;
  export let loading: boolean;
  export let loadingMinHeight: number;
  export let error: string;
  export let missing: boolean;
  export let analysis: EventAnalysisPresentation | null;
  export let analysisHasDetails: boolean;
  export let models: ChartModel[];
  export let range: [number, number] | undefined;
  export let element: HTMLElement | null = null;
  export let onClose: () => void;
  export let onRetry: () => void;
</script>

<section
  bind:this={element}
  class="event-drilldown"
  aria-label="Captured event waveforms"
  aria-keyshortcuts={keyboardActive ? 'ArrowLeft ArrowRight' : undefined}
  aria-busy={loading}
  style:min-height={loading && loadingMinHeight > 0 ? `${loadingMinHeight}px` : undefined}
>
  <header>
    <div class:event-drilldown__heading--classified={Boolean(analysis)} class="event-drilldown__heading">
      <h3>Captured event waveforms</h3>
      <time datetime={new Date(timestampSeconds * 1000).toISOString()}>{formatUtc(timestampSeconds, true)}</time>
      {#if analysis}
        <p class="event-drilldown__classification" role="status" aria-live="polite" aria-atomic="true">
          <span class="event-drilldown__cause">
            <span class="event-drilldown__classification-label">Classification:</span>
            <strong>{analysis.estimatedCause}</strong>
          </span>
          {#if analysis.headerSummary}
            <span class="event-drilldown__classification-separator" aria-hidden="true">&middot;</span>
            <span class="sr-only">. </span>
            <strong class="event-drilldown__header-summary">{analysis.headerSummary}</strong>
          {/if}
        </p>
      {/if}
    </div>
    <button class="icon-button" type="button" title="Close event waveforms" aria-label="Close event waveforms" on:click={onClose}><X size={17} /></button>
  </header>
  {#if loading}
    <div class="event-drilldown__message" role="status">
      <p>Loading captured event waveform&hellip;</p>
    </div>
  {:else if error}
    <div class="event-drilldown__message event-drilldown__message--error" role="alert">
      <p>{error}</p>
      <button class="button" type="button" on:click={onRetry}><RefreshCw size={17} aria-hidden="true" /> Retry captured waveform</button>
    </div>
  {:else}
    {#if missing}
      <div class="event-drilldown__message" role="status">
        <p>No captured event waveform is available for this event.</p>
      </div>
    {/if}
    {#if analysis && analysisHasDetails}
      <section class="event-analysis" aria-label="Event analysis">
        <details class="event-analysis__details">
          <summary>Analysis details</summary>
          {#if analysis.reason}<p class="event-analysis__reason">{analysis.reason}</p>{/if}
          <div class="event-analysis__detail-grid">
            {#if analysis.recorderTrigger || analysis.waveformSignature || analysis.contextSummary}
              <section class="event-analysis__metadata">
                <h4>Recorder and event context</h4>
                {#if analysis.recorderTrigger}<p><span>Recorder trigger:</span> <strong>{analysis.recorderTrigger}</strong></p>{/if}
                {#if analysis.waveformSignature}<p><span>Observed waveform changes:</span> <strong>{analysis.waveformSignature}</strong></p>{/if}
                {#if analysis.contextSummary}<p class="event-analysis__context">{analysis.contextSummary}</p>{/if}
              </section>
            {/if}
            {#if analysis.phenomena.length}
              <section>
                <h4>Observable phenomena</h4>
                <ul>{#each analysis.phenomena as phenomenon}<li>{phenomenon}</li>{/each}</ul>
              </section>
            {/if}
            {#if analysis.alternatives.length}
              <section>
                <h4>Alternative causes</h4>
                <ul>
                  {#each analysis.alternatives as alternative}
                    <li>{alternative.label}{#if alternative.calibratedProbability !== undefined} — {formatCalibratedProbability(alternative.calibratedProbability)}{/if}</li>
                  {/each}
                </ul>
              </section>
            {/if}
            {#if analysis.evidence.length}
              <section>
                <h4>Measured evidence</h4>
                <ul>{#each analysis.evidence as item}<li>{item}</li>{/each}</ul>
              </section>
            {/if}
            {#if analysis.limitations.length}
              <section>
                <h4>Limitations</h4>
                <ul>{#each analysis.limitations as limitation}<li>{limitation}</li>{/each}</ul>
              </section>
            {/if}
            {#if analysis.noveltyStatus}
              <section><h4>Novelty assessment</h4><p>{analysis.noveltyStatus}</p></section>
            {/if}
          </div>
          {#if analysis.waveformMeasurementGroups.length}
            <details class="event-analysis__measurements">
              <summary>Additional waveform measurements — not used in the cause estimate</summary>
              <div class="event-analysis__measurement-groups">
                {#each analysis.waveformMeasurementGroups as group}
                  <section>
                    <h4>{group.label}</h4>
                    <ul>{#each group.measurements as measurement}<li>{measurement}</li>{/each}</ul>
                  </section>
                {/each}
              </div>
            </details>
          {/if}
          {#if analysis.versions.length}<p class="event-analysis__versions">{analysis.versions.join(' · ')}</p>{/if}
        </details>
      </section>
    {/if}
    {#each models as eventModel, eventIndex (`${plotId}-event-${eventIndex}`)}
      <ChartHost model={eventModel} kind="waveform" showTitle={false} showSubtitle={false} synchronizedRange={range} onRangeChange={(nextRange) => range = nextRange} />
    {/each}
  {/if}
</section>
